import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { MaybeStyledCSS } from '@belong/types';
import {
  ROOT_SIZE,
  IScalingRoot,
  TRootSizeKeys,
  TMediaSize,
  ROOT_SIZE_THRESHOLDS,
  IMetric
} from '@belong/types/ui-components/Metric.types';
import { FONT_WEIGHT, mediaMap } from '@belong/themes';
import { Stack } from '../../ui';
import { Copy } from '../../styles/Typography';

export const resizeFontValue = (
  scalingRoot: TRootSizeKeys,
  isMobileViewport: boolean,
  scale: number,
  threshold: number,
  returnNumber: boolean = false
): FlattenSimpleInterpolation | number => {
  const rootValue = isMobileViewport ? ROOT_SIZE.mobile[scalingRoot] : ROOT_SIZE.desktop[scalingRoot];

  const scaledSize = rootValue * scale * 10; // convert rem to px for the calculation so it can be rounded to the nearest 4px
  const roundedSize = Math.round(scaledSize / 4) * 4;
  // convert back to rem for the final size
  const finalSize = roundedSize / 10 < threshold ? threshold : roundedSize / 10;

  if (returnNumber) {
    return finalSize;
  }
  return css`
    font-size: ${finalSize}rem;
    line-height: ${finalSize}rem;
  `;
};

export const Container = styled.div<IScalingRoot & Pick<IMetric, 'alignBottom'>>`
  color: ${({ theme }): string => theme.baseFontColor};
  display: flex;
  flex-direction: column;
  justify-content: end;
  line-height: 1;
  width: min-content;

  ${({ alignBottom, scalingRoot }) =>
    alignBottom &&
    scalingRoot &&
    css`
      ${mediaMap(
        ROOT_SIZE_THRESHOLDS,
        (mediaType: TMediaSize) => css`
          margin-top: ${resizeFontValue(scalingRoot, mediaType === 'mobile', 0.25, 1.5, true)}rem;
        `
      )}
    `}

  ${({ scalingRoot }) =>
    mediaMap(
      ROOT_SIZE_THRESHOLDS,
      (mediaType: TMediaSize) => css`
        font-size: ${ROOT_SIZE[mediaType][scalingRoot]}rem;
      `
    )}
`;

export const Previous = styled(Copy)<IScalingRoot>`
  color: ${({ theme }): string => theme.greyFontColor};
  white-space: nowrap;

  ${({ scalingRoot }) => {
    return css`
      ${mediaMap(ROOT_SIZE_THRESHOLDS, (mediaType: TMediaSize) =>
        resizeFontValue(scalingRoot, mediaType === 'mobile', 0.25, 1.5)
      )}
    `;
  }}
`;

export const ValueContainer = styled.div<IScalingRoot & { hasPrevious: boolean }>`
  ${({ scalingRoot, hasPrevious }) => css`
    ${mediaMap(
      ROOT_SIZE_THRESHOLDS,
      (mediaType: TMediaSize) =>
        hasPrevious &&
        css`
          margin-top: calc(${ROOT_SIZE[mediaType][scalingRoot]}rem * -0.085);
          padding-top: calc(${ROOT_SIZE[mediaType][scalingRoot]}rem * 0.1);
        `
    )}
  `}
`;

export const Currency = styled(Copy)<IScalingRoot & { exceedsThreshold: boolean }>`
  color: inherit;
  font-weight: ${FONT_WEIGHT.BOLD};

  ${({ scalingRoot, exceedsThreshold }) => css`
    ${exceedsThreshold
      ? mediaMap(ROOT_SIZE_THRESHOLDS, (mediaType: TMediaSize) =>
          resizeFontValue(scalingRoot, mediaType === 'mobile', 0.5, 2)
        )
      : `font-size: 100%;`}
  `}

  ${({ exceedsThreshold }): MaybeStyledCSS =>
    exceedsThreshold &&
    css`
      vertical-align: super;
    `}
`;

export const StaticValue = styled(Copy)`
  color: inherit;
  font-weight: ${FONT_WEIGHT.BOLD};
  font-size: inherit;
`;

export const Measurement = styled(Copy)`
  color: inherit;
  font-size: inherit;
  font-weight: ${FONT_WEIGHT.BOLD};
`;

export const Interval = styled(Copy)<IScalingRoot>`
  color: inherit;
  white-space: nowrap;

  ${({ scalingRoot }) => css`
    ${mediaMap(ROOT_SIZE_THRESHOLDS, (mediaType: TMediaSize) =>
      resizeFontValue(scalingRoot, mediaType === 'mobile', 0.25, 1.5)
    )}
  `}
`;

export const Description = styled(Copy)<IScalingRoot>`
  color: ${({ theme }): string => theme.greyFontColor};
  text-align: center;
  white-space: nowrap;
  line-height: 120%;

  ${({ scalingRoot }) => css`
    ${mediaMap(ROOT_SIZE_THRESHOLDS, (mediaType: TMediaSize) =>
      resizeFontValue(scalingRoot, mediaType === 'mobile', 0.25, 1.5)
    )}
  `}
`;

export const MetricContainer = styled.div`
  display: flex;

  > div:nth-child(2) {
    padding-left: 2.4rem;
    position: relative;
    &:before {
      content: '';
      width: 0;
      height: auto;
      border: 1px solid ${({ theme }): string => theme.borderColor};
      left: 1.2rem;
      margin: auto;
      position: absolute;
      top: 0.2rem;
      bottom: 0.2rem;
    }
  }
`;

export const StackWithTopPadding = styled(Stack)`
  &::before {
    margin-bottom: 0;
  }
`;
